import React, { useEffect, useState, useMemo } from 'react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { api } from '@/api/client';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/Table';
import { Badge } from '@/components/ui/Badge';
import { FilterBar } from '@/components/ui/FilterBar';
import { Printer, Plus, Edit, Trash, DollarSign } from 'lucide-react';
import { printDocument, buildRMAContent } from '@/utils/printDocs';
import { RMAForm } from '@/components/forms/RMAForm';
import { useAuth } from '@/context/AuthContext';
import { RMA_STATUSES, RMA_TYPES } from '@/utils/helpers';
import { exportToCSV, CSV_COLUMNS } from '@/utils/exportUtils';
import { Modal } from '@/components/ui/Modal';
import { Input, Select, Textarea } from '@/components/ui/Input';

export const RMAs = () => {
  const [rmas, setRmas] = useState([]);
  const [customers, setCustomers] = useState({});
  const [products, setProducts] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [editingRMA, setEditingRMA] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [costRMA, setCostRMA] = useState(null);
  const [costForm, setCostForm] = useState({ amount: 0, category: 'RMA / Warranty', description: '', payment_method: 'Cash', notes: '', date: new Date().toISOString().split('T')[0] });
  const [costSaving, setCostSaving] = useState(false);
  const { isAdmin, isSuperAdmin } = useAuth();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [rmaRes, custRes, prodRes] = await Promise.all([
        api.getRMAs(),
        api.getCustomers(),
        api.getProducts()
      ]);
      const sorted = rmaRes.data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setRmas(sorted);
      const cmap = {}, pmap = {};
      custRes.data.forEach(c => { cmap[c.id] = c; });
      prodRes.data.forEach(p => { pmap[p.id] = p; });
      setCustomers(cmap);
      setProducts(pmap);
    } catch (error) {
      console.error('Error loading RMAs:', error);
    }
  };

  const handlePrint = (rma) => {
    const content = buildRMAContent({
      rma,
      customer: customers[rma.customer_id],
      product: products[rma.product_id]
    });
    printDocument({ title: rma.job_id || `RMA-${rma.id.slice(0, 8)}`, content, docType: 'rma' });
  };

  const handleEdit = (rma) => {
    setEditingRMA(rma);
    setShowForm(true);
  };

  const handleDelete = async (rma) => {
    if (!window.confirm('Delete this RMA?')) return;
    try {
      await api.deleteRMA(rma.id);
      loadData();
    } catch (error) { alert('Failed to delete'); }
  };

  const handleNew = () => {
    setEditingRMA(null);
    setShowForm(true);
  };

  const openCostModal = (rma) => {
    setCostRMA(rma);
    setCostForm({
      amount: 0,
      category: 'RMA / Warranty',
      description: `Cost for ${rma.job_id || rma.id.slice(0, 8).toUpperCase()} (${rma.serial_original || ''})`,
      payment_method: 'Cash',
      notes: '',
      date: new Date().toISOString().split('T')[0]
    });
  };

  const handleSaveCost = async (ev) => {
    ev.preventDefault();
    const amt = parseFloat(costForm.amount);
    if (!amt || amt <= 0) { alert('Enter a valid amount'); return; }
    setCostSaving(true);
    try {
      await api.recordRMACost(costRMA.id, {
        ...costForm,
        amount: amt
      });
      alert('Expense recorded successfully against this RMA');
      setCostRMA(null);
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to record cost');
    } finally { setCostSaving(false); }
  };

  const getStatusVariant = (status) => {
    const map = {
      'Completed': 'success',
      'Closed': 'success',
      'Cancelled': 'danger',
      'Pending': 'warning',
      'Sent to Supplier': 'info',
      'Under Inspection': 'info',
      'Diagnosing': 'info',
      'Repaired': 'success',
      'Replaced': 'success',
      'Credited - Supplier': 'info',
      'Credited - Customer': 'info',
      'Credited': 'info'
    };
    return map[status] || 'default';
  };

  const filteredRMAs = useMemo(() => {
    const q = searchTerm.toLowerCase();
    return rmas.filter(rma => {
      if (statusFilter && rma.status !== statusFilter) return false;
      if (typeFilter && (rma.rma_type || 'Customer') !== typeFilter) return false;
      if (!q) return true;
      const customer = customers[rma.customer_id];
      const product = products[rma.product_id];
      const pi = rma.purchase_info || {};
      return (rma.job_id || '').toLowerCase().includes(q) ||
        rma.serial_original?.toLowerCase().includes(q) ||
        (rma.serial_replacement || '').toLowerCase().includes(q) ||
        rma.issue?.toLowerCase().includes(q) ||
        customer?.name?.toLowerCase().includes(q) ||
        (customer?.phone || '').includes(q) ||
        (pi.invoice_number || '').toLowerCase().includes(q) ||
        (pi.model || '').toLowerCase().includes(q) ||
        product?.name?.toLowerCase().includes(q);
    });
  }, [rmas, searchTerm, statusFilter, customers, products]);

  return (
    <div className="space-y-6" data-testid="rmas-page">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">RMAs</h1>
        <Button onClick={handleNew} data-testid="new-rma-button">
          <Plus size={20} className="mr-2" />
          New RMA
        </Button>
      </div>

      <Card>
        <FilterBar
          search={searchTerm}
          onSearchChange={setSearchTerm}
          searchPlaceholder="Search by Job ID, Serial, Customer, Phone, Invoice #, Product..."
          filters={[
            {
              key: 'type',
              label: 'All Types',
              value: typeFilter,
              options: RMA_TYPES,
              onChange: setTypeFilter
            },
            {
              key: 'status',
              label: 'All Statuses',
              value: statusFilter,
              options: RMA_STATUSES,
              onChange: setStatusFilter
            }
          ]}
          onClear={() => { setSearchTerm(''); setStatusFilter(''); setTypeFilter(''); }}
          onExport={() => exportToCSV(filteredRMAs, 'rmas', CSV_COLUMNS.rmas)}
          testId="rmas-filter"
        />
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Job ID</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Customer / Supplier</TableHead>
              <TableHead>Product</TableHead>
              <TableHead>Original Serial</TableHead>
              <TableHead>Supplier Refs</TableHead>
              <TableHead>Replacement</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Created</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredRMAs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} className="text-center py-8 text-gray-500">
                  {rmas.length === 0 ? 'No RMAs found. Click "New RMA" to create one.' : 'No RMAs match the filters.'}
                </TableCell>
              </TableRow>
            ) : (
              filteredRMAs.map((rma) => (
                <TableRow key={rma.id}>
                  <TableCell className="font-mono text-xs font-semibold">{rma.job_id || rma.id.slice(0, 8).toUpperCase()}</TableCell>
                  <TableCell>
                    <Badge variant={(rma.rma_type || 'Customer') === 'Internal' ? 'warning' : 'info'}>
                      {rma.rma_type || 'Customer'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {(rma.rma_type === 'Internal')
                      ? (rma.supplier_name || <span className="text-gray-400 italic text-xs">— stock —</span>)
                      : (customers[rma.customer_id]?.name || rma.purchase_info?.customer_name || '-')}
                  </TableCell>
                  <TableCell>{products[rma.product_id]?.name || rma.purchase_info?.product_name || '-'}</TableCell>
                  <TableCell className="font-mono text-xs">{rma.serial_original}</TableCell>
                  <TableCell className="text-[10px] font-mono space-y-0.5">
                    {rma.supplier_rma_no && <div>RMA <strong>{rma.supplier_rma_no}</strong></div>}
                    {rma.delivery_note_no && <div>DN <strong>{rma.delivery_note_no}</strong></div>}
                    {rma.replacement_note_no && <div>RN <strong>{rma.replacement_note_no}</strong></div>}
                    {rma.credit_note_no && <div>CN <strong>{rma.credit_note_no}</strong></div>}
                    {!rma.supplier_rma_no && !rma.delivery_note_no && !rma.replacement_note_no && !rma.credit_note_no && (
                      <span className="text-gray-400">—</span>
                    )}
                  </TableCell>
                  <TableCell className="font-mono text-xs">{rma.serial_replacement || <span className="text-gray-400">—</span>}</TableCell>
                  <TableCell><Badge variant={getStatusVariant(rma.status)}>{rma.status}</Badge></TableCell>
                  <TableCell>{new Date(rma.created_at).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => handleEdit(rma)} data-testid={`edit-rma-${rma.id}`}>
                        <Edit size={16} />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handlePrint(rma)} data-testid={`print-rma-${rma.id}`}>
                        <Printer size={16} />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => openCostModal(rma)} title="Record supplier/parts cost" data-testid={`cost-rma-${rma.id}`}>
                        <DollarSign size={16} className="text-amber-600" />
                      </Button>
                      {isSuperAdmin() && (
                        <Button size="sm" variant="ghost" onClick={() => handleDelete(rma)} data-testid={`delete-rma-${rma.id}`}>
                          <Trash size={16} className="text-red-600" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      <RMAForm isOpen={showForm} onClose={() => setShowForm(false)} rma={editingRMA} onSaved={loadData} />

      {/* Record Supplier / Parts Cost against an RMA -> creates Expense */}
      <Modal isOpen={!!costRMA} onClose={() => setCostRMA(null)} title={costRMA ? `Record Cost — ${costRMA.job_id || costRMA.id.slice(0, 8).toUpperCase()}` : 'Record Cost'} size="md">
        <form onSubmit={handleSaveCost} className="space-y-4">
          <p className="text-xs text-[var(--text-muted)]">
            This logs supplier shipping, courier, or replacement parts cost as an approved expense linked to this RMA.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <Input label="Amount *" type="number" step="0.01" min="0.01" value={costForm.amount}
              onChange={e => setCostForm({ ...costForm, amount: e.target.value })} data-testid="rma-cost-amount" />
            <Input label="Date *" type="date" value={costForm.date}
              onChange={e => setCostForm({ ...costForm, date: e.target.value })} data-testid="rma-cost-date" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Select label="Category" value={costForm.category}
              onChange={e => setCostForm({ ...costForm, category: e.target.value })} data-testid="rma-cost-category">
              <option>RMA / Warranty</option>
              <option>Shipping / Courier</option>
              <option>Replacement Parts</option>
              <option>Supplier RMA</option>
              <option>Other</option>
            </Select>
            <Select label="Payment Method" value={costForm.payment_method}
              onChange={e => setCostForm({ ...costForm, payment_method: e.target.value })} data-testid="rma-cost-payment">
              <option>Cash</option>
              <option>Bank Transfer</option>
              <option>Card</option>
              <option>Other</option>
            </Select>
          </div>
          <Input label="Description *" value={costForm.description}
            onChange={e => setCostForm({ ...costForm, description: e.target.value })} data-testid="rma-cost-desc" />
          <Textarea label="Notes" rows={2} value={costForm.notes}
            onChange={e => setCostForm({ ...costForm, notes: e.target.value })} data-testid="rma-cost-notes" />
          <div className="flex justify-end gap-2 pt-3 border-t border-[var(--border)]">
            <Button type="button" variant="secondary" onClick={() => setCostRMA(null)}>Cancel</Button>
            <Button type="submit" variant="primary" disabled={costSaving} data-testid="save-rma-cost">
              {costSaving ? 'Saving...' : 'Record Expense'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};
